import {useState, useEffect, FC, useRef, WheelEvent} from 'react';
import mermaid from 'mermaid';
import {ZoomIn, ZoomOut, RotateCcw, Sun, Moon, Maximize, Minimize, GitBranch} from 'lucide-react';

interface MermaidViewerSafariProps {
    diagram: string;
    isDemo?: boolean;
    onServiceClick?: (serviceUrl: string) => void;
}

const MAX_HEIGHT = 900;
const MIN_HEIGHT = 900;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 20;
const ZOOM_STEP = 0.2;

const CURVE_STYLES = ['linear', 'stepBefore', 'stepAfter', 'basis'] as const;
type CurveStyle = typeof CURVE_STYLES[number];

const lightThemeConfig = {
    theme: 'default',
    themeVariables: {},
    themeCSS: ``
};

const darkThemeConfig = {
    theme: 'dark',
    themeVariables: {},
    themeCSS: `
        g.label span.nodeLabel {
            color: #2c3e50 !important;
        }
        g.label foreignObject div span.nodeLabel {
            color: #2c3e50 !important;
        }
    `
};

const MermaidViewerSafari: FC<MermaidViewerSafariProps> = ({diagram, onServiceClick}) => {
    const [loading, setLoading] = useState(true);
    const [svg, setSvg] = useState('');
    const [zoom, setZoom] = useState(1);
    const [uniqueId] = useState(`mermaid-safari-${Math.random().toString(36).substr(2, 9)}`);
    const [isPanning, setIsPanning] = useState(false);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [startPanPosition, setStartPanPosition] = useState({x: 0, y: 0});
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [curveStyle, setCurveStyle] = useState<CurveStyle>('linear');
    const [zoomStable, setZoomStable] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Safari-specific text fix function with detailed debugging
    const applySafariTextFixes = (svgElement: SVGSVGElement) => {
        console.log('=== SAFARI TEXT FIXES DEBUG ===');
        
        // First, analyze all nodes before any changes
        const allNodes = svgElement.querySelectorAll('g.node');
        console.log(`Found ${allNodes.length} total nodes to analyze:`);
        
        allNodes.forEach((node, nodeIndex) => {
            const nodeId = node.getAttribute('id') || 'no-id';
            const rect = node.querySelector('rect');
            const polygon = node.querySelector('polygon');
            const path = node.querySelector('path');
            const foreignObj = node.querySelector('foreignObject');
            const text = node.querySelector('text');
            
            let nodeType = 'unknown';
            let shapeInfo = '';
            
            // Check for polygons and paths first, then rectangles
            if (polygon) {
                nodeType = 'polygon';
                shapeInfo = 'polygon points';
            } else if (path) {
                nodeType = 'database/cylinder';
                shapeInfo = 'custom path';
            } else if (rect) {
                const width = rect.getAttribute('width') || '0';
                const height = rect.getAttribute('height') || '0';
                // Only consider it a proper rectangle if it has actual dimensions
                if (parseFloat(width) > 0 && parseFloat(height) > 0) {
                    nodeType = 'rectangle';
                    shapeInfo = `${width}x${height}`;
                } else {
                    // 0x0 rectangles are likely subgraph/cluster containers, not actual nodes
                    nodeType = 'cluster/subgraph';
                    shapeInfo = `${width}x${height} (cluster)`;
                }
            }
            
            let textInfo = 'no-text';
            if (foreignObj) {
                const span = foreignObj.querySelector('span');
                const fWidth = foreignObj.getAttribute('width') || '0';
                const fHeight = foreignObj.getAttribute('height') || '0';
                textInfo = `foreignObject(${fWidth}x${fHeight}): "${span?.textContent || ''}"`;
            } else if (text) {
                textInfo = `text: "${text.textContent || ''}"`;
            }
            
            console.log(`Node ${nodeIndex + 1}: ${nodeId} | Type: ${nodeType} | Shape: ${shapeInfo} | Text: ${textInfo}`);
        });
        
        console.log('--- Converting foreignObjects ---');

        // Convert foreignObject elements to native SVG text with Chrome-compatible positioning
        const foreignObjects = svgElement.querySelectorAll('foreignObject');
        foreignObjects.forEach((foreignObject) => {
            const span = foreignObject.querySelector('span.nodeLabel, span.edgeLabel');
            if (!span) return;
            
            const textContent = span.textContent || '';
            const isNodeLabel = span.classList.contains('nodeLabel');
            
            if (!isNodeLabel) return; // Only handle node labels for now
            
            // Get the original foreignObject dimensions and fix if too small
            let width = parseFloat(foreignObject.getAttribute('width') || '270');
            let height = parseFloat(foreignObject.getAttribute('height') || '60');
            
            // Check if this is a rectangular node
            const parentGroup = foreignObject.closest('g.node');
            const rect = parentGroup?.querySelector('rect');
            const polygon = parentGroup?.querySelector('polygon');
            const circle = parentGroup?.querySelector('circle');
            const path = parentGroup?.querySelector('path');
            
            // Only consider it rectangular if it has a rect with actual dimensions AND no other shapes
            const isRectangularNode = !!rect && !polygon && !circle && !path && 
                parseFloat(rect.getAttribute('width') || '0') > 0 && 
                parseFloat(rect.getAttribute('height') || '0') > 0;
            
            // Explicitly check if this is a diamond/polygon node
            const isDiamondNode = !!polygon;
            
            // Explicitly check if this is a database/cylinder node
            const isDatabaseNode = !!path;
            
            console.log(`Node type detection for "${textContent}": rect=${!!rect}, polygon=${!!polygon}, circle=${!!circle}, path=${!!path}, isDatabaseNode=${isDatabaseNode}`);
            
            
            // Apply dynamic sizing to NON-rectangular shapes and clusters based on text content
            if (!isRectangularNode) {
                const textLength = textContent.length;
                const originalWidth = width;
                const originalHeight = height;
                
                // Calculate appropriate dimensions based on text length and node type
                let minWidth, minHeight;
                
                // Simple, conservative sizing for all non-rectangles
                minWidth = Math.max(80, Math.min(150, textLength * 4 + 20));
                minHeight = Math.max(30, Math.min(50, 35));
                
                // Only resize if current dimensions are too small for the text
                if (width < minWidth || height < minHeight) {
                    width = minWidth;
                    height = minHeight;
                    
                    // Update the foreignObject dimensions
                    foreignObject.setAttribute('width', width.toString());
                    foreignObject.setAttribute('height', height.toString());
                    
                    // Handle different shape types
                    const polygon = parentGroup?.querySelector('polygon.label-container');
                    const circle = parentGroup?.querySelector('circle.label-container');
                    const path = parentGroup?.querySelector('path'); // Remove .label-container for paths
                    
                    if (polygon) {
                        // Diamond shapes - use same calculation as cylinders
                        const points = polygon.getAttribute('points');
                        if (points) {
                            const textLength = textContent.length;
                            const widthMultiplier = Math.max(1.8, Math.min(3.0, textLength * 0.04 + 1.4));
                            
                            const pointsArray = points.split(' ').map(point => {
                                const [x, y] = point.split(',').map(Number);
                                return [x * widthMultiplier, y * 1.2]; // Use same multiplier as cylinders
                            });
                            const newPoints = pointsArray.map(([x, y]) => `${x},${y}`).join(' ');
                            polygon.setAttribute('points', newPoints);
                            console.log(`RESIZED DIAMOND: "${textContent}" | Multiplier: ${widthMultiplier}`);
                        }
                    } else if (circle) {
                        // Circle shapes - use same calculation as cylinders
                        const textLength = textContent.length;
                        const widthMultiplier = Math.max(1.8, Math.min(3.0, textLength * 0.04 + 1.4));
                        const baseRadius = 30; // Base circle radius
                        const newRadius = baseRadius * widthMultiplier;
                        circle.setAttribute('r', newRadius.toString());
                        console.log(`RESIZED CIRCLE: "${textContent}" | Radius: ${newRadius}`);
                    } else if (path) {
                        // For database/cylinder shapes, modify the path data directly
                        const pathData = path.getAttribute('d');
                        if (pathData && pathData.includes('a 33')) {
                            // This is a database cylinder, make it wider
                            const textLength = textContent.length;
                            const widthMultiplier = Math.max(1.8, Math.min(3.0, textLength * 0.04 + 1.4));
                            
                            // Modify the path data to make it wider
                            const newPathData = pathData
                                .replace(/a 33/g, `a ${Math.round(33 * widthMultiplier)}`) // Make ellipse wider
                                .replace(/66 0/g, `${Math.round(66 * widthMultiplier)} 0`) // Adjust width
                                .replace(/-66 0/g, `-${Math.round(66 * widthMultiplier)} 0`); // Adjust negative width
                            
                            path.setAttribute('d', newPathData);
                            console.log(`WIDENED DATABASE: "${textContent}" | Multiplier: ${widthMultiplier}`);
                        }
                    }
                    
                    console.log(`RESIZED: "${textContent}" | ${originalWidth}x${originalHeight} -> ${width}x${height} | Type: non-rectangle`);
                } else {
                    console.log(`GOOD SIZE: "${textContent}" | ${width}x${height} | Type: non-rectangle`);
                }
            } else {
                // For rectangles, ensure they have minimum dimensions
                if (width < 60 || height < 40) {
                    width = Math.max(width, 150);
                    height = Math.max(height, 60);
                    foreignObject.setAttribute('width', width.toString());
                    foreignObject.setAttribute('height', height.toString());
                    console.log(`RECTANGLE RESIZED: "${textContent}" | -> ${width}x${height} | Type: rectangle`);
                } else {
                    console.log(`RECTANGLE: "${textContent}" | ${width}x${height} | Type: rectangle`);
                }
            }
            
            // Create native SVG text element
            const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            
            // Check if there's a label group transform that we need to counteract
            const labelGroup = foreignObject.closest('g.label');
            const labelTransform = labelGroup?.getAttribute('transform') || '';
            let offsetX = 0, offsetY = 0;
            
            // Parse transform translate values to counteract them
            const transformMatch = labelTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
            if (transformMatch) {
                offsetX = -parseFloat(transformMatch[1]) || 0;
                offsetY = -parseFloat(transformMatch[2]) || 0;
                console.log(`Found label transform: ${labelTransform}, counteracting with offset: ${offsetX}, ${offsetY}`);
            }
            
            // NEW APPROACH: Fix the label group transform directly for non-rectangles
            if (!isRectangularNode && labelGroup) {
                console.log(`Fixing label group transform for non-rectangle: ${labelTransform}`);
                // For diamonds specifically, ensure perfect centering
                if (polygon) {
                    labelGroup.setAttribute('transform', 'translate(0, 0)');
                    
                    // Check if polygon has a transform that we need to counteract
                    const polygonTransform = polygon.getAttribute('transform') || '';
                    const polygonMatch = polygonTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
                    if (polygonMatch) {
                        // Counteract polygon transform to center text
                        offsetX = (-parseFloat(polygonMatch[1]) || 0) + 50;
                        offsetY = (-parseFloat(polygonMatch[2]) || 0) + 50;
                        console.log(`Centered diamond text with polygon offset: (${offsetX}, ${offsetY})`);
                    } else {
                        offsetX = 0;
                        offsetY = 0;
                        console.log(`Centered diamond text at (0,0)`);
                    }
                } else if (isDatabaseNode) {
                    labelGroup.setAttribute('transform', 'translate(0, 0)');
                    offsetX = 50; // Adjust horizontal centering
                    offsetY = 0; // Adjust vertical centering
                    console.log(`Centered database cylinder text at (0,0)`);
                } else {
                    // For other non-rectangles, remove the problematic transform entirely
                    labelGroup.setAttribute('transform', 'translate(0, 0)');
                    offsetX = 0;
                    offsetY = 0;
                    console.log(`Removed label transform for non-rectangle, centering at (0,0)`);
                }
            }
            
            // Apply the counteracting offset to center the text properly
            textElement.setAttribute('x', offsetX.toString());
            textElement.setAttribute('y', offsetY.toString());
            textElement.setAttribute('text-anchor', 'middle');
            textElement.setAttribute('dominant-baseline', 'central');
            textElement.setAttribute('alignment-baseline', 'central');
            textElement.setAttribute('class', 'nodeLabel safari-converted');
            
            // Reuse the isRectangularNode variable already declared above
            
            // Apply appropriate text styling based on node type - all with normal weight
            if (isRectangularNode) {
                // Normal weight text for rectangles
                textElement.style.fontSize = '16px';
                textElement.style.fontWeight = 'normal';
            } else if (isDiamondNode) {
                // Normal weight text for diamonds
                textElement.style.fontSize = '14px';
                textElement.style.fontWeight = 'normal';
            } else {
                // Normal weight text for other shapes (circles, paths, etc.)
                textElement.style.fontSize = '14px';
                textElement.style.fontWeight = 'normal';
            }
            textElement.style.fill = isDarkMode ? '#f8f8f2' : '#333';
            textElement.style.fontFamily = 'system-ui, -apple-system, sans-serif';
            
            // Handle text wrapping for longer text
            const words = textContent.split(' ');
            if (words.length > 2 && textContent.length > 15) {
                // Multi-line text for longer content
                const maxCharsPerLine = Math.floor(width / 8); // Estimate chars per line
                const lines = [];
                let currentLine = '';
                
                words.forEach(word => {
                    if ((currentLine + ' ' + word).length <= maxCharsPerLine) {
                        currentLine += (currentLine ? ' ' : '') + word;
                    } else {
                        if (currentLine) lines.push(currentLine);
                        currentLine = word;
                    }
                });
                if (currentLine) lines.push(currentLine);
                
                // Create tspan elements for multiple lines
                const lineHeight = 1.2;
                const fontSize = isRectangularNode ? 16 : 14;
                const totalHeight = lines.length * fontSize * lineHeight;
                const startY = -(totalHeight / 2) + (fontSize * lineHeight / 2);
                
                lines.forEach((line, lineIndex) => {
                    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
                    tspan.textContent = line;
                    tspan.setAttribute('x', offsetX.toString());
                    tspan.setAttribute('y', (offsetY + startY + lineIndex * fontSize * lineHeight).toString());
                    textElement.appendChild(tspan);
                });
            } else {
                // Single line text
                textElement.textContent = textContent;
            }
            
            // Insert the text element before the foreignObject
            foreignObject.parentNode?.insertBefore(textElement, foreignObject);
            
            // Hide the foreignObject 
            foreignObject.style.display = 'none';
            
            const finalFontSize = textElement.style.fontSize;
            const finalWeight = textElement.style.fontWeight;
            console.log(`✓ CREATED TEXT: "${textContent}" | Font: ${finalFontSize}/${finalWeight} | Type: ${isRectangularNode ? 'rectangle' : 'other'} | Position: (${offsetX},${offsetY})`);
        });

        // UNIVERSAL RECTANGLE SIZING: Apply to ALL rectangles with actual content
        console.log('--- UNIVERSAL RECTANGLE SIZING ---');
        allNodes.forEach((node, nodeIndex) => {
            const nodeId = node.getAttribute('id') || 'no-id';
            const rect = node.querySelector('rect.basic.label-container');
            
            if (rect) {
                const currentWidth = parseFloat(rect.getAttribute('width') || '0');
                const currentHeight = parseFloat(rect.getAttribute('height') || '0');
                
                // Skip zero-dimension rectangles (they're usually containers)
                if (currentWidth === 0 && currentHeight === 0) {
                    (rect as HTMLElement).style.setProperty('display', 'none', 'important');
                    return;
                }
                
                // Get text content for this node
                const textElement = node.querySelector('text.safari-converted, .nodeLabel');
                const textContent = textElement?.textContent || '';
                const textLength = textContent.length;
                
                // Skip if no meaningful text content
                if (textLength === 0) return;
                
                console.log(`RESIZING RECTANGLE ${nodeIndex + 1}: ${nodeId} | Current: ${currentWidth}x${currentHeight} | Text: "${textContent}"`);
                
                // Use same calculation as cylinders
                const widthMultiplier = Math.max(1.8, Math.min(3.0, textLength * 0.04 + 1.4));
                const baseWidth = Math.max(120, currentWidth); // Use current width as base if larger
                const newWidth = baseWidth * widthMultiplier;
                
                rect.setAttribute('width', newWidth.toString());
                rect.setAttribute('height', '60');
                rect.setAttribute('x', (-newWidth / 2).toString()); // Center the rectangle
                rect.setAttribute('y', '-30');
                rect.setAttribute('rx', '6');
                rect.setAttribute('ry', '6');
                
                // Strong CSS overrides
                (rect as HTMLElement).style.setProperty('width', `${newWidth}px`, 'important');
                (rect as HTMLElement).style.setProperty('height', '60px', 'important');
                (rect as HTMLElement).style.setProperty('fill', isDarkMode ? '#44475a' : '#f0f4ff', 'important');
                (rect as HTMLElement).style.setProperty('stroke', isDarkMode ? '#bd93f9' : '#4b72b0', 'important');
                (rect as HTMLElement).style.setProperty('stroke-width', '2px', 'important');
                (rect as HTMLElement).style.setProperty('opacity', '1', 'important');
                (rect as HTMLElement).style.setProperty('visibility', 'visible', 'important');
                (rect as HTMLElement).style.setProperty('display', 'block', 'important');
                
                console.log(`✓ RESIZED RECTANGLE: "${textContent}" | ${baseWidth} -> ${newWidth} | Multiplier: ${widthMultiplier}`);
            }
        });

        // Final analysis of all elements after conversion
        console.log('--- FINAL STATE ANALYSIS ---');
        allNodes.forEach((node, nodeIndex) => {
            const nodeId = node.getAttribute('id') || 'no-id';
            const rect = node.querySelector('rect');
            const polygon = node.querySelector('polygon');
            const path = node.querySelector('path');
            const visibleForeignObj = node.querySelector('foreignObject:not([style*="display: none"])');
            const text = node.querySelector('text.safari-converted');
            const originalText = node.querySelector('text:not(.safari-converted)');
            
            let shapeStatus = 'no-shape';
            if (rect) {
                const width = rect.getAttribute('width') || '0';
                const height = rect.getAttribute('height') || '0';
                const visibility = getComputedStyle(rect).visibility;
                const opacity = getComputedStyle(rect).opacity;
                shapeStatus = `rect(${width}x${height}) visible:${visibility} opacity:${opacity}`;
            } else if (polygon) {
                shapeStatus = 'polygon(visible)';
            } else if (path) {
                shapeStatus = 'path(visible)';
            }
            
            let textStatus = 'no-text';
            if (text) {
                const fontSize = (text as HTMLElement).style.fontSize;
                const content = text.textContent || '';
                textStatus = `converted-text(${fontSize}): "${content}"`;
            } else if (visibleForeignObj) {
                textStatus = 'foreignObject(still-visible)';
            } else if (originalText) {
                textStatus = 'original-text';
            }
            
            console.log(`FINAL Node ${nodeIndex + 1}: ${nodeId} | Shape: ${shapeStatus} | Text: ${textStatus}`);
        });
        
        // Ensure all text elements have proper attributes
        const allTexts = svgElement.querySelectorAll('text:not(.safari-converted)');
        allTexts.forEach((text) => {
            text.setAttribute('text-anchor', 'middle');
            text.setAttribute('dominant-baseline', 'central');
        });
        
        console.log('=== END SAFARI DEBUG ===');
    };

    // Function to adjust subgraph sizes after node modifications
    const adjustSubgraphSizes = (svgElement: SVGSVGElement) => {
        console.log('=== ADJUSTING SUBGRAPH SIZES ===');
        
        // Find all nodes and their widths (rectangles, cylinders, etc.)
        const allNodes = svgElement.querySelectorAll('g.node');
        const nodeWidths = new Map();
        
        allNodes.forEach(nodeGroup => {
            const nodeId = nodeGroup.getAttribute('data-id') || nodeGroup.getAttribute('id');
            if (!nodeId) return;
            
            let nodeWidth = 0;
            
            // Check for rectangles
            const rect = nodeGroup.querySelector('rect.basic.label-container');
            if (rect) {
                nodeWidth = parseFloat(rect.getAttribute('width') || '0');
            }
            
            // Check for database cylinders
            const path = nodeGroup.querySelector('path[d*="a "]');
            if (path) {
                const pathData = path.getAttribute('d') || '';
                const widthMatch = pathData.match(/a (\d+)/);
                if (widthMatch) {
                    const ellipseWidth = parseInt(widthMatch[1]);
                    nodeWidth = ellipseWidth * 2; // Diameter
                }
            }
            
            // Check for polygons (diamonds)
            const polygon = nodeGroup.querySelector('polygon');
            if (polygon) {
                const points = polygon.getAttribute('points');
                if (points) {
                    const pointsArray = points.split(' ').map(point => {
                        const [x] = point.split(',').map(Number);
                        return Math.abs(x);
                    });
                    nodeWidth = Math.max(...pointsArray) * 2; // Max width
                }
            }
            
            // Check for circles
            const circle = nodeGroup.querySelector('circle');
            if (circle) {
                const radius = parseFloat(circle.getAttribute('r') || '0');
                nodeWidth = radius * 2; // Diameter
            }
            
            if (nodeWidth > 0) {
                nodeWidths.set(nodeId, nodeWidth);
                console.log(`NODE: ${nodeId} has width ${nodeWidth}`);
            }
        });
        
        // Now check subgraphs and find which nodes belong to them
        const subgraphs = svgElement.querySelectorAll('g.cluster');
        subgraphs.forEach((subgraph) => {
            const rect = subgraph.querySelector('rect');
            if (!rect) return;
            
            const subgraphId = subgraph.getAttribute('id');
            console.log(`CHECKING SUBGRAPH: ${subgraphId}`);
            
            // Get subgraph bounds
            const x = parseFloat(rect.getAttribute('x') || '0');
            const y = parseFloat(rect.getAttribute('y') || '0');
            const width = parseFloat(rect.getAttribute('width') || '0');
            const height = parseFloat(rect.getAttribute('height') || '0');
            
            let maxRequiredWidth = 0;
            
            // Check each node to see if it's within this subgraph bounds
            allNodes.forEach(nodeGroup => {
                const transform = nodeGroup.getAttribute('transform') || '';
                const match = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
                if (match) {
                    const nodeX = parseFloat(match[1]);
                    const nodeY = parseFloat(match[2]);
                    
                    // Check if node is within subgraph bounds
                    if (nodeX >= x && nodeX <= x + width && nodeY >= y && nodeY <= y + height) {
                        const nodeId = nodeGroup.getAttribute('data-id') || nodeGroup.getAttribute('id');
                        const nodeWidth = nodeWidths.get(nodeId) || 0;
                        maxRequiredWidth = Math.max(maxRequiredWidth, nodeWidth);
                        console.log(`NODE ${nodeId} (${nodeWidth}px) is inside subgraph ${subgraphId}`);
                    }
                }
            });
            
            if (maxRequiredWidth > 0) {
                const padding = 150; // Extra padding around nodes
                const newWidth = Math.max(width, maxRequiredWidth + padding);
                
                if (newWidth > width) {
                    rect.setAttribute('width', newWidth.toString());
                    console.log(`EXPANDED SUBGRAPH ${subgraphId}: ${width} -> ${newWidth}`);
                    
                    // Also adjust the subgraph label position if needed
                    const label = subgraph.querySelector('g.cluster-label');
                    if (label) {
                        const labelTransform = label.getAttribute('transform') || '';
                        const labelMatch = labelTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
                        if (labelMatch) {
                            const labelX = parseFloat(labelMatch[1]);
                            const labelY = parseFloat(labelMatch[2]);
                            // Center the label in the new width
                            const newLabelX = x + newWidth / 2;
                            label.setAttribute('transform', `translate(${newLabelX}, ${labelY})`);
                            console.log(`RECENTERED LABEL: ${labelX} -> ${newLabelX}`);
                        }
                    }
                } else {
                    console.log(`SUBGRAPH ${subgraphId}: No expansion needed (${width}px is sufficient)`);
                }
            } else {
                console.log(`SUBGRAPH ${subgraphId}: No database nodes found inside`);
            }
        });
        
        // Update SVG viewBox to accommodate expanded subgraphs
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
            const [minX, minY, width, height] = viewBox.split(' ').map(Number);

            // Find the rightmost subgraph edge
            let maxRight = width;
            subgraphs.forEach(subgraph => {
                const rect = subgraph.querySelector('rect');
                if (rect) {
                    const x = parseFloat(rect.getAttribute('x') || '0');
                    const w = parseFloat(rect.getAttribute('width') || '0');
                    maxRight = Math.max(maxRight, x + w + 200); // 50px margin
                }
            });

            if (maxRight > width) {
                svgElement.setAttribute('viewBox', `${minX} ${minY} ${maxRight} ${height}`);
                console.log(`EXPANDED SVG VIEWBOX: ${width} -> ${maxRight}`);
            }
        }
        
        console.log('=== END SUBGRAPH ADJUSTMENT ===');
    };

    const renderDiagram = async () => {
        try {
            setLoading(true);
            const currentTheme = isDarkMode ? darkThemeConfig : lightThemeConfig;

            mermaid.initialize({
                startOnLoad: true,
                securityLevel: "loose",
                maxTextSize: 99999999,
                fontFamily: "system-ui, -apple-system, sans-serif",
                ...currentTheme,
                flowchart: {
                    useMaxWidth: true,
                    htmlLabels: true,
                    curve: curveStyle,
                    nodeSpacing: 120,
                    rankSpacing: 150,
                    padding: 50,
                    diagramPadding: 50,
                    // Force proper node dimensions for Safari
                    wrappingWidth: 300, // Minimum node width
                    // Specific sizing for different node types
                },
                sequence: {
                    useMaxWidth: true,
                    showSequenceNumbers: true,
                },
                themeVariables: {
                    ...currentTheme.themeVariables,
                    primaryColor: isDarkMode ? '#44475a' : '#f0f4ff',
                    primaryTextColor: isDarkMode ? '#f8f8f2' : '#333',
                    primaryBorderColor: isDarkMode ? '#bd93f9' : '#4b72b0',
                    lineColor: isDarkMode ? '#f8f8f2' : '#333333',
                    nodeBkg: isDarkMode ? '#44475a' : '#f0f4ff',
                    nodeTextColor: isDarkMode ? '#f8f8f2' : '#333',
                },
                themeCSS: `
                    ${currentTheme.themeCSS || ''}

                    /* Rectangle styling - minimal since rectangles are now visible */
                    g.flowchart-node rect.basic.label-container,
                    .node rect.basic.label-container {
                        fill: ${isDarkMode ? '#44475a' : '#f0f4ff'} !important;
                        stroke: ${isDarkMode ? '#bd93f9' : '#4b72b0'} !important;
                        stroke-width: 2px !important;
                        rx: 6 !important;
                        ry: 6 !important;
                    }

                    /* Safari's proportional text sizing - keep original */
                    .nodeLabel,
                    span.nodeLabel,
                    .flowchart-label {
                        font-size: 14px !important;
                        font-weight: normal !important;
                        color: ${isDarkMode ? '#f8f8f2' : '#333'} !important;
                        line-height: 1.2 !important;
                        font-family: system-ui, -apple-system, sans-serif !important;
                        text-anchor: middle !important;
                        dominant-baseline: central !important;
                    }
                    
                    /* Smaller text for rectangular nodes */
                    g.flowchart-node rect.basic.label-container + foreignObject .nodeLabel,
                    g.flowchart-node rect.basic.label-container + foreignObject span.nodeLabel,
                    .node rect.basic.label-container + foreignObject .nodeLabel {
                        font-size: 16px !important;
                        font-weight: normal !important;
                        white-space: pre-wrap !important;
                        word-wrap: break-word !important;
                    }

                    /* Safari's dynamic foreignObject sizing - keep original flexible approach */
                    foreignObject {
                        overflow: visible !important;
                    }

                    foreignObject div {
                        width: 100% !important;
                        height: 100% !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        padding: 8px !important;
                        box-sizing: border-box !important;
                        text-align: center !important;
                        font-size: 14px !important;
                        font-weight: normal !important;
                    }
                    
                    /* Keep foreignObject sizing flexible for rectangles */
                    g.flowchart-node rect.basic.label-container + foreignObject,
                    .node rect.basic.label-container + foreignObject {
                        width: 180px !important;
                        height: 50px !important;
                    }

                    /* Database cylinder shapes - ensure visibility and proper styling */
                    g.node path {
                        display: block !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                        fill: ${isDarkMode ? '#44475a' : '#f0f4ff'} !important;
                        stroke: ${isDarkMode ? '#bd93f9' : '#4b72b0'} !important;
                        stroke-width: 2px !important;
                    }

                    /* Database cylinder foreignObjects - wider dimensions */
                    g.node path + g.label foreignObject {
                        width: 200px !important;
                        height: 80px !important;
                    }

                    /* Edge labels */
                    .edgeLabel {
                        font-size: 12px !important;
                        font-weight: normal !important;
                        font-family: system-ui, -apple-system, sans-serif !important;
                    }

                    /* Safari converted text styling */
                    text.safari-converted {
                        font-family: system-ui, -apple-system, sans-serif !important;
                        user-select: none !important;
                        text-anchor: middle !important;
                        dominant-baseline: central !important;
                    }

                    /* Safari converted text - default for non-rectangles */
                    text.nodeLabel.safari-converted {
                        font-size: 14px !important;
                        font-weight: normal !important;
                        fill: ${isDarkMode ? '#f8f8f2' : '#333'} !important;
                    }
                    
                    /* Safari converted text - smaller for rectangles */
                    g.flowchart-node rect.basic.label-container ~ text.nodeLabel.safari-converted,
                    .node rect.basic.label-container ~ text.nodeLabel.safari-converted {
                        font-size: 16px !important;
                        font-weight: normal !important;
                    }

                    text.edgeLabel.safari-converted {
                        font-size: 12px !important;
                        font-weight: normal !important;
                        fill: ${isDarkMode ? '#f8f8f2' : '#666'} !important;
                    }

                    /* Clickable nodes */
                    a.clickable rect.basic.label-container,
                    .clickable rect.basic.label-container {
                        stroke: ${isDarkMode ? '#a78bfa' : '#8b5cf6'} !important;
                        stroke-width: 2.5px !important;
                        fill: ${isDarkMode ? '#44475a' : '#faf5ff'} !important;
                        animation: pulse 2.5s ease-in-out infinite !important;
                        cursor: pointer !important;
                    }

                    @keyframes pulse {
                        0% { filter: drop-shadow(0 0 3px ${isDarkMode ? 'rgba(189, 147, 249, 0.5)' : 'rgba(139, 92, 246, 0.4)'}); }
                        50% { filter: drop-shadow(0 0 8px ${isDarkMode ? 'rgba(167, 139, 250, 0.7)' : 'rgba(139, 92, 246, 0.6)'}); }
                        100% { filter: drop-shadow(0 0 3px ${isDarkMode ? 'rgba(189, 147, 249, 0.5)' : 'rgba(139, 92, 246, 0.4)'}); }
                    }

                    /* Edge paths */
                    .edgePaths path, .edgePath path, .flowchart-link {
                        stroke: ${isDarkMode ? '#f8f8f2' : '#333333'} !important;
                        stroke-width: 2px !important;
                        fill: none !important;
                    }
                `
            });

            const {svg} = await mermaid.render(uniqueId, diagram);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = svg;
            const svgElement = tempDiv.querySelector('svg');

            if (svgElement) {
                const viewBox = svgElement.getAttribute('viewBox')?.split(' ').map(Number) || [0, 0, 0, 0];
                const [, , width, height] = viewBox;
                const aspectRatio = width / height;
                const newHeight = Math.min(MAX_HEIGHT, height);
                const newWidth = newHeight * aspectRatio;

                svgElement.setAttribute('width', `${newWidth}`);
                svgElement.setAttribute('height', `${newHeight}`);
                svgElement.style.maxWidth = '100%';

                // Apply Safari fixes immediately
                applySafariTextFixes(svgElement);
                
                // Adjust subgraph sizes to accommodate wider database cylinders
                adjustSubgraphSizes(svgElement);

                setSvg(tempDiv.innerHTML);

            } else {
                setSvg(svg);
            }
        } catch (error) {
            console.error('Failed to render diagram:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        renderDiagram();
    }, [diagram, uniqueId, isDarkMode, curveStyle]);

    // Add click handler for service links
    useEffect(() => {
        if (!onServiceClick || !svg) return;

        const svgContainer = svgContainerRef.current;
        if (!svgContainer) return;

        // Wait for SVG to be fully rendered in DOM
        const timer = setTimeout(() => {
            const handleClick = (e: Event) => {
                const mouseEvent = e as MouseEvent;
                const target = mouseEvent.target as HTMLElement;

                // Find the closest link element
                const link = target.closest('a') ||
                             target.closest('[data-node="true"]') ||
                             (target.tagName === 'A' ? target : null);

                if (link) {
                    const href = link.getAttribute('href') || link.getAttribute('xlink:href');
                    console.log('Safari diagram click detected:', href);

                    if (href && href.startsWith('/')) {
                        mouseEvent.preventDefault();
                        mouseEvent.stopPropagation();
                        mouseEvent.stopImmediatePropagation();
                        console.log('Calling onServiceClick with:', href);
                        onServiceClick(href);
                        return false;
                    }
                }
            };

            // Add listener to the actual SVG element
            const svgElement = svgContainer.querySelector('svg');
            if (svgElement) {
                // Add handlers to all links directly
                const allLinks = svgElement.querySelectorAll('a[href], a[xlink\\:href]');
                allLinks.forEach((link) => {
                    link.addEventListener('click', handleClick, { capture: true });
                });

                svgElement.addEventListener('click', handleClick, { capture: true });
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [svg, onServiceClick]);

    // Stable zoom handling to prevent cascading
    const handleZoom = (delta: number) => {
        if (!zoomStable || !containerRef.current || !svgContainerRef.current) return;
        
        setZoomStable(false);
        
        const newZoom = Math.min(Math.max(zoom + delta, MIN_ZOOM), MAX_ZOOM);
        const scaleFactor = newZoom / zoom;
        const newX = position.x * scaleFactor;
        const newY = position.y * scaleFactor;
        
        setZoom(newZoom);
        setPosition({x: newX, y: newY});
        
        // Clear existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }
        
        // Set new timeout to stabilize zoom
        zoomTimeoutRef.current = setTimeout(() => {
            setZoomStable(true);
        }, 100);
    };

    const handleZoomIn = () => handleZoom(ZOOM_STEP);
    const handleZoomOut = () => handleZoom(-ZOOM_STEP);

    const resetView = () => {
        setZoom(1);
        setPosition({x: 0, y: 0});
    };

    const handleThemeToggle = () => {
        setIsDarkMode(!isDarkMode);
    };

    const toggleCurveStyle = () => {
        const currentIndex = CURVE_STYLES.indexOf(curveStyle);
        const nextIndex = (currentIndex + 1) % CURVE_STYLES.length;
        setCurveStyle(CURVE_STYLES[nextIndex]);
    };

    const toggleFullscreen = () => {
        if (!containerRef.current) return;

        if (!isFullscreen) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isFullscreen) {
                toggleFullscreen();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);

    // Add Safari gesture event listeners with better isolation
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (!isSafari) return;

        // Better wheel event isolation for Safari
        const safariWheelHandler = (e: Event) => {
            const wheelEvent = e as unknown as WheelEvent;
            if (!zoomStable) return;
            
            // Always stop propagation to prevent page scroll
            wheelEvent.stopPropagation();
            
            // Only prevent default for zoom gestures
            if (wheelEvent.ctrlKey || Math.abs(wheelEvent.deltaY) > 0) {
                wheelEvent.preventDefault();
                
                let delta = 0;
                if (wheelEvent.ctrlKey) {
                    delta = wheelEvent.deltaY * -0.02; // Pinch
                } else {
                    delta = wheelEvent.deltaY * -0.006; // Scroll
                }
                
                handleZoom(delta);
            }
        };

        // Add Safari-specific gesture listeners
        container.addEventListener('gesturestart', handleGestureStart, { passive: false });
        container.addEventListener('gesturechange', handleGestureChange, { passive: false });
        container.addEventListener('gestureend', handleGestureEnd, { passive: false });
        container.addEventListener('wheel', safariWheelHandler, { passive: false });

        return () => {
            container.removeEventListener('gesturestart', handleGestureStart);
            container.removeEventListener('gesturechange', handleGestureChange);
            container.removeEventListener('gestureend', handleGestureEnd);
            container.removeEventListener('wheel', safariWheelHandler);
        };
    }, [zoomStable, handleZoom]);

    // Chrome/Firefox wheel handling (Safari uses native event listener)
    const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
        if (!zoomStable) return;
        
        // Safari detection - skip React handler for Safari (uses native listener)
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) return; // Safari handled by native event listener
        
        // Chrome/Firefox handling
        e.preventDefault();
        e.stopPropagation();
        
        let delta = 0;
        if (e.ctrlKey || e.metaKey) {
            delta = e.deltaY * -0.01;
        } else {
            delta = e.deltaY * -0.005;
        }
        
        handleZoom(delta);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsPanning(true);
        setStartPanPosition({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isPanning) {
            const newX = e.clientX - startPanPosition.x;
            const newY = e.clientY - startPanPosition.y;
            setPosition({x: newX, y: newY});
        }
    };

    const handleMouseUp = () => {
        setIsPanning(false);
    };

    const handleMouseEnter = () => {
        // Mouse enter handler
    };

    const handleMouseLeave = () => {
        setIsPanning(false);
    };

    // Safari touch gesture handlers
    const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
    const [_touchCenter, setTouchCenter] = useState<{x: number, y: number} | null>(null);

    const getTouchDistance = (touches: React.TouchList) => {
        if (touches.length < 2) return null;
        const touch1 = touches[0];
        const touch2 = touches[1];
        return Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) + 
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );
    };

    const getTouchCenter = (touches: React.TouchList) => {
        if (touches.length < 2) return null;
        const touch1 = touches[0];
        const touch2 = touches[1];
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        // Check if touch is specifically on interactive elements (button, text) in Safari warning
        const target = e.target as HTMLElement;
        const isOnSafariButton = target.tagName === 'BUTTON' || target.closest('button');
        const isOnSafariWarning = target.closest('.safari-warning-overlay');
        
        // Only allow default behavior for actual button clicks, not touch gestures
        if (isOnSafariWarning && isOnSafariButton && e.touches.length === 1) {
            // Allow default behavior only for single finger button clicks
            return;
        }
        
        // Always prevent default for all other interactions to stop page zoom
        e.preventDefault();
        
        if (e.touches.length === 2) {
            // Pinch start - stop any panning
            setIsPanning(false);
            const distance = getTouchDistance(e.touches);
            const center = getTouchCenter(e.touches);
            setLastTouchDistance(distance);
            setTouchCenter(center);
            console.log('Pinch started:', distance);
        } else if (e.touches.length === 1) {
            // Single touch for panning (only if not on Safari warning)
            if (!showSafariWarning) {
                const touch = e.touches[0];
                setIsPanning(true);
                setStartPanPosition({
                    x: touch.clientX - position.x,
                    y: touch.clientY - position.y
                });
            }
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        // Always prevent default to stop page scrolling and zooming
        e.preventDefault();
        
        if (e.touches.length === 2 && lastTouchDistance) {
            // Ultra responsive pinch zoom
            const distance = getTouchDistance(e.touches);
            if (distance) {
                const rawDelta = distance - lastTouchDistance;
                const delta = rawDelta * 1; // Much higher sensitivity for immediate response
                
                // Remove threshold for maximum fluidity
                handleZoom(delta);
                setLastTouchDistance(distance);
            }
        } else if (e.touches.length === 1 && isPanning && !showSafariWarning) {
            // Single touch panning (only if Safari warning is not showing)
            const touch = e.touches[0];
            const newX = touch.clientX - startPanPosition.x;
            const newY = touch.clientY - startPanPosition.y;
            setPosition({x: newX, y: newY});
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        // Check if touch is specifically on interactive elements (button) in Safari warning
        const target = e.target as HTMLElement;
        const isOnSafariButton = target.tagName === 'BUTTON' || target.closest('button');
        const isOnSafariWarning = target.closest('.safari-warning-overlay');
        
        // Only allow default behavior for actual button clicks
        if (isOnSafariWarning && isOnSafariButton && e.touches.length === 0) {
            // Allow default behavior only for button clicks
            return;
        }
        
        // Prevent default for all other interactions
        e.preventDefault();
        
        if (e.touches.length === 0) {
            // All touches ended
            setIsPanning(false);
            setLastTouchDistance(null);
            setTouchCenter(null);
        } else if (e.touches.length === 1) {
            // Transition from pinch to pan
            setLastTouchDistance(null);
            setTouchCenter(null);
            
            // Set up for single touch panning
            const touch = e.touches[0];
            setIsPanning(true);
            setStartPanPosition({
                x: touch.clientX - position.x,
                y: touch.clientY - position.y
            });
        }
    };

    // Enhanced Safari gesture handlers for fluid pinch-to-zoom
    const [lastGestureScale, setLastGestureScale] = useState(1);
    const [showSafariWarning, setShowSafariWarning] = useState(() => {
        // Check localStorage to see if user has dismissed the warning before
        return localStorage.getItem('safariWarningDismissed') !== 'true';
    });

    const handleGestureStart = (e: any) => {
        e.preventDefault();
        setLastGestureScale(1);
        console.log('Gesture start');
    };

    const handleGestureChange = (e: any) => {
        e.preventDefault();
        if (e.scale) {
            // Ultra responsive gesture scaling
            const scaleChange = e.scale / lastGestureScale;
            const delta = (scaleChange - 1) * 20.0; // Extremely high sensitivity for immediate response
            
            // Remove threshold completely for maximum fluidity
            handleZoom(delta);
            setLastGestureScale(e.scale);
        }
    };

    const handleGestureEnd = (e: any) => {
        e.preventDefault();
        setLastGestureScale(1);
        console.log('Gesture end');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 w-full">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"/>
            </div>
        );
    }

    return (
        <div
            className={`relative w-full transition-colors duration-300 ${
                isDarkMode ? 'bg-[#282a36]' : 'bg-gray-50'
            }`}
            style={{
                maxHeight: isFullscreen ? '100vh' : `${MAX_HEIGHT}px`,
                minHeight: isFullscreen ? '100vh' : `${MIN_HEIGHT}px`,
                touchAction: 'pan-x pan-y pinch-zoom',
                userSelect: 'none',
            }}
        >
            {/* Fullscreen Close Button - Only visible in fullscreen */}
            {isFullscreen && (
                <div className="absolute top-4 left-4 z-20">
                    <button
                        onClick={toggleFullscreen}
                        className="p-3 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400"
                        aria-label="Exit fullscreen"
                        title="Exit fullscreen (ESC)"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="absolute top-4 right-4 flex gap-2 bg-opacity-80 p-2 rounded-lg shadow-md z-10"
                 style={{
                     backgroundColor: isDarkMode ? '#44475a' : '#ffffff'
                 }}>
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    isDarkMode ? 'text-[#f8f8f2] bg-[#282a36]' : 'text-gray-700 bg-gray-100'
                }`}>
                    Safari • {Math.round(zoom * 100)}%
                </div>

                <button
                    onClick={toggleFullscreen}
                    className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                    {isFullscreen ? <Minimize className="w-5 h-5"/> : <Maximize className="w-5 h-5"/>}
                </button>

                <button
                    onClick={toggleCurveStyle}
                    className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label="Toggle curve style"
                    title="Toggle curve style"
                >
                    <GitBranch className="w-5 h-5"/>
                </button>

                <button
                    onClick={handleThemeToggle}
                    className={`hidden p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                    {isDarkMode ? <Sun className="w-5 h-5"/> : <Moon className="w-5 h-5"/>}
                </button>

                <button
                    onClick={handleZoomIn}
                    className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label="Zoom in"
                    title="Zoom in"
                >
                    <ZoomIn className="w-5 h-5"/>
                </button>

                <button
                    onClick={handleZoomOut}
                    className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label="Zoom out"
                    title="Zoom out"
                >
                    <ZoomOut className="w-5 h-5"/>
                </button>

                <button
                    onClick={resetView}
                    className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]' : 'hover:bg-gray-100 focus:ring-blue-500 text-gray-700'
                    }`}
                    aria-label="Reset view"
                    title="Reset view"
                >
                    <RotateCcw className="w-5 h-5"/>
                </button>
            </div>

            {/* Enhanced Legend Panel - Same as original */}
            <div className="absolute right-4 bg-opacity-95 p-3 rounded-xl shadow-xl z-10 max-w-xs border"
                 style={{
                     backgroundColor: '#ffffff',
                     borderColor: '#e2e8f0',
                     top: '120px',
                     width: '280px'
                 }}>
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">✨</span>
                    <div className="text-sm font-bold text-[#7c3aed]">
                        Ready to Build?
                    </div>
                </div>

                {/* Main message - More compact */}
                <div className="text-xs leading-relaxed mb-2 text-gray-700">
                    Click on the <span className="font-bold px-1 py-0.5 rounded text-[#7c3aed] bg-[#faf5ff]">glowing services</span> to
                    explore implementation details.
                </div>

                {/* Visual indicators - More compact */}
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                        <div
                            className="w-5 h-2.5 rounded border-2 animate-pulse flex-shrink-0"
                            style={{
                                borderColor: '#8b5cf6',
                                backgroundColor: 'rgba(139, 92, 246, 0.15)'
                            }}
                        ></div>
                        <span className="text-xs text-gray-600">
                Interactive Service
            </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <div
                            className="w-5 h-2.5 rounded border-2 flex-shrink-0"
                            style={{
                                borderColor: '#6b7280',
                                backgroundColor: '#f8fafc'
                            }}
                        ></div>
                        <span className="text-xs text-gray-600">
                Standard Component
            </span>
                    </div>
                </div>
            </div>

            <div
                ref={containerRef}
                className={`w-full h-full overflow-hidden cursor-grab active:cursor-grabbing ${
                    isFullscreen ? 'fixed inset-0 z-50' : ''
                }`}
                style={{
                    backgroundColor: isDarkMode ? '#282a36' : '#f9fafb',
                    touchAction: 'pan-x pan-y pinch-zoom', // Allow panning and pinch zoom
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                }}
                tabIndex={0}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Safari warning overlay */}
                {showSafariWarning && (
                    <div className="safari-warning-overlay absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20" style={{touchAction: 'auto'}}>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-lg" style={{touchAction: 'auto'}}>
                            <div className="flex items-center mb-4">
                                <div className="text-orange-500 text-2xl mr-3">⚠️</div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Safari Compatibility Notice</h3>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 mb-4">
                                You are using Safari. This diagram may have display issues including oversized shapes, incorrect text positioning, or rendering problems. 
                                For optimal diagram visualization and the best experience, we strongly recommend using Chrome or Firefox.
                            </p>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                <strong>Safari controls:</strong> Pinch to zoom • Drag to pan
                            </div>
                            <button
                                onClick={() => {
                                    localStorage.setItem('safariWarningDismissed', 'true');
                                    setShowSafariWarning(false);
                                }}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchMove={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                            >
                                OK, Continue
                            </button>
                        </div>
                    </div>
                )}
                
                <div
                    ref={svgContainerRef}
                    className="w-full h-full flex items-center justify-center"
                    style={{
                        transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                        transformOrigin: 'center center',
                        transition: 'none', // Remove all transitions for immediate response
                    }}
                    dangerouslySetInnerHTML={{__html: svg}}
                />
            </div>
        </div>
    );
};

export default MermaidViewerSafari;